import { z } from 'zod';
import { PermissionCodeSchema, RoleIdSchema, RoleRefSchema } from '../rbac/index.js';
import { UserSchema, PasswordSchema } from './users.js';

export const LoginInputSchema = z.object({
  email: z.string().email(),
  password: PasswordSchema,
});

export const RefreshTokenSchema = z.string().regex(/^mrt_[A-Za-z0-9_-]{43}$/);
export type RefreshToken = z.infer<typeof RefreshTokenSchema>;

/**
 * @aiPurpose declare the wire shape of `permissions` carried alongside a
 *   `RoleRef` inside `LoginResponse.user` and `GET /api/auth/me`.
 * @aiInvariant either the literal `'ALL'` (ADMIN short-circuit) or an
 *   array of `PermissionCode`. Empty arrays are valid.
 * @aiSecurity guards the client-side `RequirePermission` route gate.
 */
export const PermissionsBundleSchema = z.union([z.literal('ALL'), z.array(PermissionCodeSchema)]);

/**
 * @aiPurpose `'ALL'` sentinel or an array of permission codes.
 * @aiInvariant see `PermissionsBundleSchema`.
 * @aiSecurity see `PermissionsBundleSchema`.
 */
export type PermissionsBundle = z.infer<typeof PermissionsBundleSchema>;

// WHY: extracted so every login/refresh/me schema below shares the same
// projection of `User & { permissions: PermissionsBundle }`. Inlining the
// extension at four call sites left strict-parse coverage for
// `permissions` implicit at each — a future schema add could silently drop
// the bundle without a single test failing.
const userWithPermissions = UserSchema.extend({
  permissions: PermissionsBundleSchema,
});

/**
 * @aiPurpose response shape of `POST /api/auth/login` — the wire envelope
 *   that lets a client hydrate its session in a single round-trip. This
 *   is the transport-agnostic UMBRELLA shape: it accepts both the web
 *   branch (no `refreshToken`) and the mobile branch (with `refreshToken`).
 *   Per-transport callers SHOULD prefer `WebLoginResponseSchema` /
 *   `MobileLoginResponseSchema` so the wire shape they accept matches the
 *   transport they actually negotiated; this umbrella is reserved for the
 *   backend controller's typed return where a single signature must cover
 *   both branches.
 * @aiInvariant `user.permissions` is a `PermissionsBundle` evaluated at
 *   login-time from the user's current `roleId`; future role/permission
 *   mutations require a re-login or a `/me` refresh to propagate.
 * @aiInvariant `refreshToken` is OPTIONAL on the wire because the web
 *   transport is cookie-only: the rotated refresh token rides the
 *   httpOnly Secure SameSite=Strict cookie and is NEVER echoed in the
 *   JSON body, so a same-origin XSS cannot exfiltrate it via a body
 *   read. The mobile branch (`X-Refresh-Client: mobile`) carries the
 *   refresh token in the body and emits no Set-Cookie. Clients MUST
 *   NOT depend on `refreshToken` being present on the web transport.
 * @aiSecurity carries the access token + permission bundle. The refresh
 *   token rides the httpOnly Secure SameSite=Strict cookie for web
 *   (canonical carrier) and the response body for mobile (no Set-Cookie).
 */
export const LoginResponseSchema = z
  .object({
    accessToken: z.string().min(1),
    refreshToken: RefreshTokenSchema.optional(),
    user: userWithPermissions,
  })
  .strict();

/**
 * @aiPurpose transport-pinned response shape of `POST /api/auth/login`
 *   for the WEB SPA. Web is cookie-only — the refresh token rides the
 *   httpOnly Secure SameSite=Strict cookie exclusively and is NEVER
 *   echoed in the JSON body — so the wire shape MUST omit `refreshToken`.
 * @aiInvariant `.strict()` rejects any incoming `refreshToken` field
 *   (including `undefined`, since extra keys are unrecognized). A backend
 *   regression that re-introduces a body refresh token on the web path
 *   trips this parse and surfaces the contract drift loudly instead of
 *   silently letting the token reach `mutation.data`. Without this, the
 *   web client's defense-in-depth boundary in
 *   `packages/shared-hooks/src/auth/useLogin.ts` would be the only line
 *   of defense.
 * @aiSecurity load-bearing — pins the cookie-only invariant for the web
 *   transport at the wire boundary, defense-in-depth alongside
 *   `WebLoginResult.refreshToken?: never`.
 */
export const WebLoginResponseSchema = z
  .object({
    accessToken: z.string().min(1),
    user: userWithPermissions,
  })
  .strict();
export type WebLoginResponse = z.infer<typeof WebLoginResponseSchema>;

/**
 * @aiPurpose transport-pinned response shape of `POST /api/auth/login`
 *   for the MOBILE app (`X-Refresh-Client: mobile`). Mobile carries the
 *   refresh token in the JSON body so it can store it in SecureStore;
 *   the backend emits NO Set-Cookie on this branch.
 * @aiInvariant `refreshToken` is REQUIRED on this transport — a mobile
 *   response missing it has nothing to persist and cannot rotate. The
 *   schema's `.strict()` rejects unknown keys.
 * @aiSecurity carries the refresh token in the body. The mobile client is
 *   the only consumer that may read it; web callers MUST use
 *   `WebLoginResponseSchema` so a mis-routed mobile response cannot ride
 *   into the web SPA via a permissive parse.
 */
export const MobileLoginResponseSchema = z
  .object({
    accessToken: z.string().min(1),
    refreshToken: RefreshTokenSchema,
    user: userWithPermissions,
  })
  .strict();
export type MobileLoginResponse = z.infer<typeof MobileLoginResponseSchema>;

/**
 * @aiPurpose response shape of `POST /api/auth/refresh` — the
 *   transport-agnostic UMBRELLA. Accepts both the web shape (no body
 *   token) and the mobile shape (with rotated body token). Per-transport
 *   callers SHOULD prefer `WebRefreshResponseSchema` /
 *   `MobileRefreshResponseSchema`; this umbrella exists for the backend
 *   controller's typed return.
 * @aiInvariant `refreshToken` is OPTIONAL on the wire because the web
 *   transport is cookie-only: the rotated refresh token rides the
 *   httpOnly Secure SameSite=Strict cookie and is NEVER echoed in the
 *   JSON body. The mobile branch (`X-Refresh-Client: mobile`) carries
 *   the rotated token in the body and emits no Set-Cookie.
 * @aiSecurity see `LoginResponseSchema`.
 */
export const RefreshResponseSchema = z
  .object({
    accessToken: z.string().min(1),
    refreshToken: RefreshTokenSchema.optional(),
  })
  .strict();
export type RefreshResponse = z.infer<typeof RefreshResponseSchema>;

/**
 * @aiPurpose transport-pinned response shape of `POST /api/auth/refresh`
 *   for the WEB SPA. The rotated refresh token rides the httpOnly
 *   Secure SameSite=Strict cookie exclusively; the body is `accessToken`
 *   only.
 * @aiInvariant `.strict()` rejects any incoming `refreshToken` field. A
 *   backend regression that re-introduces a body refresh token on the
 *   web path trips this parse in `apps/web/src/api/refresh.ts` and is
 *   rewrapped as `NetworkError` (preserving the session) — surfacing the
 *   contract drift loudly instead of letting the token leak into the SPA.
 * @aiSecurity load-bearing — pins the cookie-only invariant for the web
 *   refresh transport at the wire boundary.
 */
export const WebRefreshResponseSchema = z
  .object({
    accessToken: z.string().min(1),
  })
  .strict();
export type WebRefreshResponse = z.infer<typeof WebRefreshResponseSchema>;

/**
 * @aiPurpose transport-pinned response shape of `POST /api/auth/refresh`
 *   for the MOBILE app (`X-Refresh-Client: mobile`). The rotated token
 *   rides the body so SecureStore can replace its previous value; the
 *   backend emits NO Set-Cookie on this branch.
 * @aiInvariant `refreshToken` is REQUIRED — a mobile rotation response
 *   missing it has nothing to persist and would orphan the family on
 *   the next rotation attempt. `.strict()` rejects unknown keys.
 * @aiSecurity carries the rotated refresh token in the body. Web
 *   callers MUST use `WebRefreshResponseSchema` to keep the cookie-only
 *   invariant intact.
 */
export const MobileRefreshResponseSchema = z
  .object({
    accessToken: z.string().min(1),
    refreshToken: RefreshTokenSchema,
  })
  .strict();
export type MobileRefreshResponse = z.infer<typeof MobileRefreshResponseSchema>;

export const RefreshInputSchema = z
  .object({
    refreshToken: RefreshTokenSchema,
  })
  .strict();
export type RefreshInput = z.infer<typeof RefreshInputSchema>;

/**
 * @aiPurpose validate the JWT access-token payload accepted by the
 *   backend `JwtAuthGuard`.
 * @aiInvariant `roleId` is a branded UUID; old payloads carrying the
 *   pre-#92 `role: 'admin'|'manager'|'logist_cn'` literal field MUST fail
 *   to parse so the guard rejects them with `INVALID_TOKEN` (401).
 * @aiInvariant `tv` (token version, #386) is a non-negative integer
 *   that the backend compares against the live `User.tokenVersion`
 *   column on every authenticated request. The field is OPTIONAL on the
 *   wire and defaults to `0` here so this PR is the "expand" phase of
 *   an expand/contract rollout — old instances during a rolling deploy
 *   that mint pre-#386 access tokens (no `tv` claim) keep parsing on
 *   new instances, and the live-state compare still rejects any token
 *   whose absent-or-zero `tv` lags the row's bumped `tokenVersion`. The
 *   "contract" phase tightens this to `.strict()` + required once all
 *   live tokens are tv-bearing (one access-TTL window after rollout);
 *   tracked as a P3 follow-up.
 * @aiSecurity load-bearing — single source of access-token shape for
 *   every authenticated route on the backend.
 */
export const JwtPayloadSchema = z
  .object({
    sub: z.string().uuid(),
    roleId: RoleIdSchema,
    jti: z.string().uuid(),
    // INVARIANT (#386 expand phase): default to 0 when absent so the
    //   rolling-deploy window does not force every session to re-login.
    //   The row's tokenVersion also defaults to 0, so an absent `tv`
    //   parses to 0 and matches the row — until the operator runs a
    //   password reset, at which point the row bumps to 1 and the
    //   absent-tv token correctly fails the live-state compare.
    tv: z.number().int().nonnegative().optional().default(0),
    iat: z.number().int(),
    exp: z.number().int(),
  })
  .strict();

/**
 * @aiPurpose body shape of `POST /api/auth/login`.
 * @aiInvariant see `LoginInputSchema`.
 * @aiSecurity see `LoginResponseSchema`.
 */
export type LoginInput = z.infer<typeof LoginInputSchema>;

/**
 * @aiPurpose typed projection of `LoginResponseSchema`.
 * @aiInvariant see `LoginResponseSchema`.
 * @aiSecurity see `LoginResponseSchema`.
 */
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

/**
 * @aiPurpose typed projection of the JWT access-token payload.
 * @aiInvariant see `JwtPayloadSchema` — `roleId` is branded UUID.
 * @aiSecurity see `JwtPayloadSchema`.
 */
export type JwtPayload = z.infer<typeof JwtPayloadSchema>;

/**
 * @aiPurpose response shape of `GET /api/auth/me`.
 * @aiInvariant equals `User & { role: RoleRef; permissions: ...; isLastOnShift; isOnShift }`.
 *   `permissions` is derived per-request from the caller's `roleId`.
 *   `isLastOnShift` is `null` for non-`logist_cn` callers, `true` when the
 *   caller is the only `active && isOnShift` `logist_cn` and is currently
 *   on shift, `false` otherwise. The flag is recomputed live on every
 *   `/me` call from a fresh DB count — never cached, never stored in the
 *   JWT, so toggling shift on the server side does NOT require a JWT
 *   re-issue; the client refreshes the value by re-calling `/me`.
 *   The Zod field is `.nullable().optional().default(null)` to keep
 *   the schema tolerant of pre-#315 backends and test fixtures during
 *   rolling deploys (expand-and-contract phase 1: expand). A future
 *   PR will tighten to required after every consumer reliably emits
 *   the field.
 *
 *   `isOnShift` is OWNED here as the sole wire surface for the live
 *   logist shift flag. `UserSchema` no longer carries the field, so
 *   admin endpoints (POST/PATCH /users, deactivate, reactivate, list)
 *   cannot read or pre-position a logist's shift state. Required, not
 *   optional: every backend reliably emits the explicit
 *   `isOnShift: row.isOnShift` line in `AuthController.me` after the
 *   `...publicUser` spread, so a missing top-level value is a contract
 *   bug, not a deploy-skew artefact.
 * @aiSecurity see `LoginResponseSchema`.
 */
export const MeResponseSchema = userWithPermissions
  .extend({
    isLastOnShift: z.boolean().nullable().optional().default(null),
    isOnShift: z.boolean(),
  })
  .strict();

/**
 * @aiPurpose authenticated user projection returned by `/api/auth/me`.
 * @aiInvariant see `MeResponseSchema`.
 * @aiSecurity see `MeResponseSchema`.
 */
export type MeResponse = z.infer<typeof MeResponseSchema>;

export const ChangePasswordInputSchema = z
  .object({
    currentPassword: PasswordSchema,
    newPassword: PasswordSchema,
  })
  .strict()
  .refine((v) => v.currentPassword !== v.newPassword, {
    message: 'newPassword must differ from currentPassword',
    path: ['newPassword'],
  });
export type ChangePasswordInput = z.infer<typeof ChangePasswordInputSchema>;

export const ChangePasswordResponseSchema = z.object({ ok: z.literal(true) }).strict();
export type ChangePasswordResponse = z.infer<typeof ChangePasswordResponseSchema>;

export const ResetPasswordInputSchema = z
  .object({
    newPassword: PasswordSchema,
  })
  .strict();
export type ResetPasswordInput = z.infer<typeof ResetPasswordInputSchema>;

export const ResetPasswordResponseSchema = z.object({ ok: z.literal(true) }).strict();
export type ResetPasswordResponse = z.infer<typeof ResetPasswordResponseSchema>;

export { RoleRefSchema };
